import { useCallback } from 'react'
import { isSameDay, differenceInDays } from 'date-fns'
import { useUnitAvailabilitySuspenseQuery, useOrderSummaryQuery } from '@/api/accommodation/hooks'
import type { AccommodationDetail } from '@/api/accommodation/types'
import { Button } from '@/components/ui/button'
import { Link } from '@tanstack/react-router'
import { trackReservationDatesSelected, trackReservationProceeded } from '@/lib/track'
import { useTrackOnChange } from '@/hooks/useTrackOnce'
import { useDateSelection } from './useDateSelection'
import { getDaysLabel, getNightsLabel } from '@/lib/utils'
import { PriceSummaryInline } from '@/components/money/PriceSummaryInline'
import { DateSelectionSummarySkeleton } from './DateSelectionSummarySkeleton'
import { getDateSelectionWindow } from './dateSelectionWindow'

interface DateSelectionSummaryProps {
  accommodation: AccommodationDetail
}

export const DateSelectionSummary = ({ accommodation }: DateSelectionSummaryProps) => {
  const { selectedFrom: dateFrom, selectedTo: dateTo } = useDateSelection()
  const { data: availabilityData } = useUnitAvailabilitySuspenseQuery(accommodation.unit.id)

  // Get the stay length limits from the selected arrival date's API data
  const arrivalData = dateFrom ? availabilityData.find((a) => isSameDay(a.date, dateFrom)) : undefined
  const minNights = arrivalData?.minNights ?? null
  const maxNights = arrivalData?.maxNights ?? null

  // Width of the location's offer window; the derived window is only a fallback
  // for API responses that predate the offerAheadDays field.
  const availabilityWindow = getDateSelectionWindow(availabilityData)
  const offerWindowDays = availabilityWindow ? (accommodation.offerAheadDays ?? availabilityWindow.days) : null

  // No dates selected or only arrival selected
  if (!dateFrom || !dateTo) {
    const showMinNights = dateFrom && !dateTo && minNights
    return (
      <DateSelectionSummaryEmpty
        offerWindowDays={offerWindowDays}
        isSelectingDeparture={!!dateFrom}
        minNights={showMinNights ? minNights : null}
        maxNights={showMinNights ? maxNights : null}
      />
    )
  }

  // Both dates selected - render price summary (this will suspend for order summary)
  return <DateSelectionSummaryPrice accommodation={accommodation} dateFrom={dateFrom} dateTo={dateTo} />
}

interface DateSelectionSummaryEmptyProps {
  offerWindowDays: number | null
  isSelectingDeparture: boolean
  minNights: number | null
  maxNights: number | null
}

const DateSelectionSummaryEmpty = ({
  offerWindowDays,
  isSelectingDeparture,
  minNights,
  maxNights,
}: DateSelectionSummaryEmptyProps) => {
  return (
    <>
      <div className="flex min-h-12 flex-col justify-center">
        <div className="text-xl font-semibold text-slate-900">
          {isSelectingDeparture ? 'Vyberte odjezd' : 'Vyberte příjezd'}
        </div>
        {minNights ? (
          <div className="text-sm text-slate-500">
            Minimálně {minNights} {getNightsLabel(minNights)}
            {maxNights !== null && `, nejvýš ${maxNights} ${getNightsLabel(maxNights)}`}
          </div>
        ) : offerWindowDays ? (
          <p className="text-sm text-slate-500">
            Last-minute termíny na {offerWindowDays} {getDaysLabel(offerWindowDays)}
          </p>
        ) : (
          <p className="text-sm text-slate-500">Momentálně bez volných termínů</p>
        )}
      </div>
      <Button disabled>Rezervovat</Button>
    </>
  )
}

interface DateSelectionSummaryPriceProps {
  accommodation: AccommodationDetail
  dateFrom: Date
  dateTo: Date
}

const DateSelectionSummaryPrice = ({ accommodation, dateFrom, dateTo }: DateSelectionSummaryPriceProps) => {
  const { setSelectedFrom, setSelectedTo } = useDateSelection()
  const orderSummaryRequest = {
    start: dateFrom,
    end: dateTo,
    unitId: accommodation.unit.id,
    pets: false,
    beds: 1,
    cribs: 0,
  }

  const { data, isLoading, isError } = useOrderSummaryQuery(orderSummaryRequest)

  const nights = differenceInDays(dateTo, dateFrom)

  // Track when dates are selected/changed
  const datesKey = data?.available ? `${dateFrom.toISOString()}-${dateTo.toISOString()}` : null

  const trackDateChange = useCallback(() => {
    if (data?.available) {
      const prices = {
        totalStandardPrice: data.standardPrice,
        totalDiscountPrice: data.discountPrice,
      }
      trackReservationDatesSelected(accommodation, prices, dateFrom, dateTo)
    }
  }, [accommodation, data, dateFrom, dateTo])

  useTrackOnChange(datesKey, trackDateChange, !!data?.available)

  const handleClearDates = () => {
    setSelectedFrom(null)
    setSelectedTo(null)
  }

  // Error state (network, etc.) - check before data to handle errors properly
  if (isError) {
    return (
      <>
        <div className="flex min-h-12 flex-col justify-center">
          <div className="text-base font-medium text-red-600">Chyba při načítání ceny</div>
          <p className="text-sm text-slate-500">Zkuste to prosím znovu.</p>
        </div>
        <Button variant="outline" onClick={handleClearDates}>
          Změnit termín
        </Button>
      </>
    )
  }

  // Loading state
  if (isLoading || !data) {
    return <DateSelectionSummarySkeleton />
  }

  // Dates unavailable (e.g., already booked)
  if (!data.available) {
    return (
      <>
        <div className="flex min-h-12 flex-col justify-center">
          <div className="text-base font-medium text-red-600">Termín není dostupný</div>
          <p className="text-sm text-slate-500">Zvolený termín již není k dispozici.</p>
        </div>
        <Button variant="secondary" onClick={handleClearDates}>
          Změnit termín
        </Button>
      </>
    )
  }

  return (
    <>
      <PriceSummaryInline discountPrice={data.discountPrice} standardPrice={data.standardPrice} nights={nights} />
      <Button asChild>
        <Link
          to="/ubytovani/$slug/$unitSlug/rezervace"
          params={{
            slug: accommodation.slug,
            unitSlug: accommodation.unit.slug,
          }}
          search={{
            dateFrom: dateFrom,
            dateTo: dateTo,
          }}
          resetScroll={true}
          onClick={() => {
            const prices = {
              totalStandardPrice: data.standardPrice,
              totalDiscountPrice: data.discountPrice,
            }
            trackReservationProceeded(accommodation, prices, dateFrom, dateTo)
          }}
        >
          Rezervovat
        </Link>
      </Button>
    </>
  )
}
